import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { printerBaseUrl, eposTestPrint } from '../../lib/eposPrint';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Printer, Save, ShieldCheck, ExternalLink, Loader2 } from 'lucide-react';

/**
 * Configurazione della STAMPANTE CAMERIERE dal profilo cameriere.
 *
 * Perché qui: la stampa ePOS diretta verso una stampante di rete in HTTPS con
 * certificato autofirmato funziona solo se IL BROWSER DEL DISPOSITIVO ha accettato
 * quel certificato. Dando al cameriere questa schermata, può aprire l'IP della
 * stampante e accettare il certificato dal proprio dispositivo, poi fare una prova
 * di stampa — senza dover passare dalla cassa/Admin.
 */
export default function WaiterPrinter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [wp, setWp] = useState({ enabled: false, protocol: 'https', host: '', port: 0, paper_width: 80 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get('/print/waiter-printer')
      .then(({ data }) => { if (data?.waiter_printer) setWp((w) => ({ ...w, ...data.waiter_printer })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (field, value) => setWp((w) => ({ ...w, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/print/waiter-printer', {
        enabled: !!wp.enabled,
        protocol: wp.protocol || 'https',
        host: wp.host || '',
        port: Number(wp.port) || 0,
        paper_width: Number(wp.paper_width) || 80,
      });
      if (data?.waiter_printer) setWp((w) => ({ ...w, ...data.waiter_printer }));
      toast.success(t('waiterPrinter.saved', { defaultValue: 'Stampante salvata' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('waiterPrinter.saveError', { defaultValue: 'Errore nel salvataggio' }));
    } finally { setSaving(false); }
  };

  // Apre l'IP della stampante in una nuova scheda: qui il browser mostra l'avviso
  // del certificato autofirmato e il cameriere può accettarlo una volta per tutte.
  const acceptCertificate = () => {
    if (!wp.host) { toast.error(t('waiterPrinter.hostRequired', { defaultValue: 'Inserisci prima l\u2019indirizzo IP della stampante' })); return; }
    const url = printerBaseUrl({ host: wp.host, protocol: wp.protocol || 'https', port: Number(wp.port) || 0 });
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.info(t('waiterPrinter.acceptHint', { defaultValue: 'Accetta il certificato nella nuova scheda, poi torna qui e fai una prova di stampa.' }));
  };

  const testPrint = async () => {
    if (!wp.host) { toast.error(t('waiterPrinter.hostRequired', { defaultValue: 'Inserisci prima l\u2019indirizzo IP della stampante' })); return; }
    setTesting(true);
    try {
      await eposTestPrint(
        { host: wp.host, protocol: wp.protocol || 'https', port: Number(wp.port) || 0, paper_width: Number(wp.paper_width) || 80 },
        'Vexel POS',
        null,
      );
      toast.success(t('waiterPrinter.testSent', { defaultValue: 'Stampa di prova inviata' }));
    } catch (e) {
      toast.error(e?.message || t('waiterPrinter.testFailed', { defaultValue: 'Stampa di prova fallita' }));
    } finally { setTesting(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-5" data-testid="waiter-printer-page">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/waiter')} data-testid="waiter-printer-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <div className="font-display font-semibold text-lg leading-tight flex items-center gap-2">
            <Printer className="h-5 w-5" /> {t('waiterPrinter.title', { defaultValue: 'Stampante cameriere' })}
          </div>
          <div className="text-xs text-muted-foreground leading-tight">
            {t('waiterPrinter.subtitle', { defaultValue: 'Configura e attiva la stampante da questo dispositivo' })}
          </div>
        </div>
      </div>

      <Card className="p-5">
        <p className="text-sm text-muted-foreground mb-4">
          {t('waiterPrinter.desc', { defaultValue: 'Stampante di rete (Wi-Fi/Ethernet) su cui escono resoconto e scontrino fiscale dei camerieri. Per usarla in HTTPS devi prima accettare il certificato dal tuo dispositivo.' })}
        </p>

        <div className="flex items-center justify-between gap-4 py-2">
          <div>
            <div className="font-medium">{t('waiterPrinter.enable', { defaultValue: 'Attiva stampante cameriere' })}</div>
            <div className="text-sm text-muted-foreground">{t('waiterPrinter.enableHint', { defaultValue: 'I resoconti/scontrini usciranno da questa stampante.' })}</div>
          </div>
          <Switch checked={!!wp.enabled} onCheckedChange={(v) => set('enabled', v)} disabled={loading} data-testid="waiter-printer-enable" />
        </div>

        {wp.enabled && (
          <div className="mt-3 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>{t('waiterPrinter.host', { defaultValue: 'Indirizzo IP stampante' })}</Label>
                <Input
                  value={wp.host || ''}
                  onChange={(e) => set('host', e.target.value)}
                  placeholder="192.168.1.50"
                  className="mt-1.5 font-mono"
                  inputMode="decimal"
                  data-testid="waiter-printer-host"
                />
              </div>
              <div>
                <Label>{t('waiterPrinter.paper', { defaultValue: 'Larghezza carta' })}</Label>
                <Select value={String(wp.paper_width || 80)} onValueChange={(v) => set('paper_width', Number(v))}>
                  <SelectTrigger className="mt-1.5" data-testid="waiter-printer-paper"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="80">80mm</SelectItem>
                    <SelectItem value="58">58mm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('waiterPrinter.protocol', { defaultValue: 'Protocollo' })}</Label>
                <Select value={wp.protocol || 'https'} onValueChange={(v) => set('protocol', v)}>
                  <SelectTrigger className="mt-1.5" data-testid="waiter-printer-protocol"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="https">HTTPS</SelectItem>
                    <SelectItem value="http">HTTP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('waiterPrinter.port', { defaultValue: 'Porta (0 = default)' })}</Label>
                <Input
                  type="number"
                  value={wp.port ?? 0}
                  onChange={(e) => set('port', Number(e.target.value))}
                  placeholder="0"
                  className="mt-1.5 font-mono"
                  data-testid="waiter-printer-port"
                />
              </div>
            </div>

            {/* Passo chiave: accettare il certificato dal dispositivo del cameriere */}
            <div className="rounded-xl border border-border bg-muted/40 p-3">
              <div className="text-sm font-medium flex items-center gap-2 mb-1">
                <ShieldCheck className="h-4 w-4 text-primary" />
                {t('waiterPrinter.certTitle', { defaultValue: 'Accetta il certificato della stampante' })}
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {t('waiterPrinter.certDesc', { defaultValue: 'Necessario una sola volta per dispositivo con stampanti HTTPS. Apri l\u2019indirizzo, procedi/accetta l\u2019avviso di sicurezza, poi torna qui.' })}
              </p>
              <Button type="button" variant="outline" onClick={acceptCertificate} className="rounded-xl w-full" data-testid="waiter-printer-accept-cert">
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('waiterPrinter.openPrinter', { defaultValue: 'Apri stampante e accetta certificato' })}
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="button" onClick={save} disabled={saving} className="rounded-xl flex-1" data-testid="waiter-printer-save">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {t('common.save', { defaultValue: 'Salva' })}
              </Button>
              <Button type="button" variant="outline" onClick={testPrint} disabled={testing} className="rounded-xl flex-1" data-testid="waiter-printer-test">
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
                {t('waiterPrinter.test', { defaultValue: 'Prova di stampa' })}
              </Button>
            </div>
          </div>
        )}

        {!wp.enabled && (
          <div className="mt-3 flex justify-end">
            <Button type="button" onClick={save} disabled={saving} className="rounded-xl" data-testid="waiter-printer-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {t('common.save', { defaultValue: 'Salva' })}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
